import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import deDE from '@angular/common/locales/de';
import enEN from '@angular/common/locales/en';
import esES from '@angular/common/locales/es';
import frFR from '@angular/common/locales/fr';
import itIT from '@angular/common/locales/it';
import nlNL from '@angular/common/locales/nl';
import { APP_INITIALIZER, ModuleWithProviders, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { CWT_API_CONFIG, CWT_CACHE_CONFIG, CwtApiConfig } from '@cawita/core-front/api';
import { CWT_ASSET_CONFIG, CwtAssetConfig } from '@cawita/core-front/asset';
import { CWT_AUTH_CONFIG, CWT_AUTH_USER_FACTORY, CwtAuthModule, CwtAuthStoreService, JwtTokenInterceptor } from '@cawita/core-front/auth';
import { CWT_STORAGE_PROVIDER, CwtLocalStorageProvider } from '@cawita/core-front/storage';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { AuthConfig } from '@mobile/config/auth.config';
import { CacheConfig } from '@mobile/config/cache.config';
import { IonicConfig } from '@mobile/config/ionic.config';
import { ErrorInterceptor } from '@mobile/interceptors/error.interceptor';
import { AuthFlowService, initializeAuthFlow } from '@mobile/services/commons/auth-flow.service';
import { KatchaAuthStore } from '@mobile/services/stores/katcha-auth.store';
import { CrudModule } from '@shared/modules/crud';
import { CwtLanguageConfig, CwtLanguageModule } from '@shared/modules/language';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';
import { AnnouncementSocket } from '../announcement/announcement-socket';
import { SOCKET_LISTENER } from '../socket/socket-listener';
import { STAY_LISTENER } from '../stay/stay-listener';
import { CWT_CAPTCHA_CONFIG, CwtCaptchaConfig } from './captcha.config';
import { CWT_APP_CONFIG, CwtAppConfig } from './core-app.config';
import { KatchaAssetModule } from '@shared/pipes';
import * as models from '@shared/models';

registerLocaleData(frFR);
registerLocaleData(enEN);
registerLocaleData(deDE);
registerLocaleData(itIT);
registerLocaleData(esES);
registerLocaleData(nlNL);

export interface KatchaCoreConfig {
  api: CwtApiConfig;
  app: CwtAppConfig;
  asset: CwtAssetConfig;
  captcha: CwtCaptchaConfig;
  language: CwtLanguageConfig;
  socket: SocketIoConfig;
}

@NgModule({
  exports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule,
  ],
  declarations: [
  ],
  providers: [],
})
export class KatchaCoreModule {
  static forRoot(config: KatchaCoreConfig): ModuleWithProviders<KatchaCoreModule> {
    return {
      ngModule: KatchaCoreModule,
      providers: [
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: HTTP_INTERCEPTORS, multi: true, useClass: JwtTokenInterceptor },
        { provide: HTTP_INTERCEPTORS, multi: true, useClass: ErrorInterceptor },
        { provide: CWT_STORAGE_PROVIDER, useClass: CwtLocalStorageProvider },
        { provide: CwtAuthStoreService, useClass: KatchaAuthStore },
        { provide: CWT_AUTH_CONFIG, useValue: { ...AuthConfig, navigation: AuthConfig.navigation } },
        { provide: CWT_CACHE_CONFIG, useValue: CacheConfig },
        { provide: CWT_CAPTCHA_CONFIG, useValue: config.captcha },
        { provide: CWT_AUTH_USER_FACTORY, useValue: (user) => new models.User(user) },
        { provide: CWT_APP_CONFIG, useValue: config.app },
        { provide: CWT_API_CONFIG, useValue: config.api },
        { provide: CWT_ASSET_CONFIG, useValue: config.asset },
        { provide: APP_INITIALIZER, multi: true, deps: [AuthFlowService], useFactory: initializeAuthFlow },
        { provide: STAY_LISTENER, multi: true, useExisting: AnnouncementSocket },
        { provide: SOCKET_LISTENER, multi: true, useExisting: AnnouncementSocket },
        ...CrudModule.forRoot(models).providers,
        ...IonicModule.forRoot(IonicConfig).providers,
        ...CwtLanguageModule.forRoot(config.language).providers,
        ...KatchaAssetModule.forRoot().providers,
        ...SocketIoModule.forRoot(config.socket).providers
      ]
    };
  }
}